import { Button } from '../Button'
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from '../Form'
import { FormFieldLabel } from '../FormFields'
import type { FormFieldProps } from '../FormFields'
import type { EnumValueMeta } from '@joyful/utils'
import { cn, createEnumSchema } from '@joyful/utils'
import type { LucideIcon } from 'lucide-react'
import {
  ArrowDownLeftIcon,
  ArrowDownRightIcon,
  ArrowUpLeftIcon,
  ArrowUpRightIcon,
} from 'lucide-react'
import type { ControllerProps, FieldPath, FieldValues } from 'react-hook-form'
import type { ReactNode, SVGProps } from 'react'
import type { Corner } from './CornerLayoutContext'

interface CornerPositionMeta extends EnumValueMeta {
  expandIcon: LucideIcon
  indicatorOffset: { x: number; y: number }
  minimizeIcon: LucideIcon
}

export type LeftCorner = Extract<Corner, 'bottom-left' | 'top-left'>

export const LeftCornerPositionSchema = createEnumSchema<
  LeftCorner,
  CornerPositionMeta
>([
  {
    expandIcon: ArrowUpRightIcon,
    indicatorOffset: { x: 3, y: 12 },
    minimizeIcon: ArrowDownLeftIcon,
    title: 'Bottom left',
    value: 'bottom-left',
  },
  {
    expandIcon: ArrowDownRightIcon,
    indicatorOffset: { x: 3, y: 7 },
    minimizeIcon: ArrowUpLeftIcon,
    title: 'Top left',
    value: 'top-left',
  },
])

export const CornerPositionSchema = createEnumSchema<
  Corner,
  CornerPositionMeta
>([
  ...LeftCornerPositionSchema.definitions,
  {
    expandIcon: ArrowUpLeftIcon,
    indicatorOffset: { x: 16, y: 12 },
    minimizeIcon: ArrowDownRightIcon,
    title: 'Bottom right',
    value: 'bottom-right',
  },
  {
    expandIcon: ArrowDownLeftIcon,
    indicatorOffset: { x: 16, y: 7 },
    minimizeIcon: ArrowUpRightIcon,
    title: 'Top right',
    value: 'top-right',
  },
])

interface CornerPositionIconProps extends SVGProps<SVGSVGElement> {
  position: Corner
}

function CornerPositionIcon({ position, ...props }: CornerPositionIconProps) {
  const meta = CornerPositionSchema.meta(position)
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      {...props}
    >
      <rect x={1} y={5} width={22} height={14} rx={2} />
      <rect
        x={meta.indicatorOffset.x}
        y={meta.indicatorOffset.y}
        width={5}
        height={5}
        rx={1}
      />
    </svg>
  )
}

export type CornerPositionDefinition =
  (typeof CornerPositionSchema.definitions)[number]

interface CornerPositionButtonGroupProps {
  className?: string
  definitions?: ReadonlyArray<CornerPositionDefinition>
  onChange: (value: Corner) => void
  value: Corner
}

function CornerPositionButtonGroup({
  className,
  definitions = CornerPositionSchema.definitions,
  onChange,
  value,
}: CornerPositionButtonGroupProps) {
  return (
    <div
      className={cn(
        'grid auto-cols-fr grid-flow-col gap-x-2 self-stretch',
        className,
      )}
    >
      {definitions.map((definition) => {
        const isSelected = value === definition.value
        return (
          <div
            key={definition.value}
            className={cn(
              isSelected &&
                '**:data-[slot=button]:bg-tremor-background-muted **:data-[slot=button]:border-tremor-content-emphasis **:data-[slot=button]:shadow-[0_0_0_1px_var(--tremor-content-emphasis)]',
            )}
          >
            <Button
              variant="outline"
              type="button"
              width="full"
              colorVariant={isSelected ? 'default' : 'muted'}
              onClick={() => {
                onChange(definition.value)
              }}
            >
              <span className="flex flex-col items-center gap-y-1">
                <CornerPositionIcon
                  className="size-8"
                  position={definition.value}
                />
                <span className="text-xs">{definition.title}</span>
              </span>
            </Button>
          </div>
        )
      })}
    </div>
  )
}

export interface CornerPositionFormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>
  extends
    Omit<ControllerProps<TFieldValues, TName>, 'render'>,
    Pick<FormFieldProps, 'className' | 'description'> {
  definitions?: ReadonlyArray<CornerPositionDefinition>
  label?: ReactNode
}

export function CornerPositionFormField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  className,
  definitions,
  description,
  label,
  ...controllerProps
}: CornerPositionFormFieldProps<TFieldValues, TName>) {
  return (
    <FormField
      render={({ field: { onChange, ref: _, value } }) => {
        return (
          <FormItem className={className}>
            {label === undefined ? null : (
              <FormFieldLabel>{label}</FormFieldLabel>
            )}

            <FormControl>
              <CornerPositionButtonGroup
                definitions={definitions}
                value={value}
                onChange={onChange}
              />
            </FormControl>

            {typeof description === 'string' ? (
              <FormDescription>{description}</FormDescription>
            ) : (
              description
            )}

            <FormMessage />
          </FormItem>
        )
      }}
      {...controllerProps}
    />
  )
}
